import { call, put, takeLatest } from "redux-saga/effects";

import { hasCookie, refreshAccessToken, showHttpErrorNotification, tokenHandler } from "@shared/utils";
import { navigate, showNotification, startLoading, stopLoading } from "@shared/store/actions";
import { ActionWithPayload, ResponseError, WithCallback } from "@shared/interfaces";
import { CSRF_COOKIE_NAME, NameOfRoutes } from "@shared/constants";
import { UserModel } from "@shared/models";

import { LoginShape, RegistrationShape, RestoreShape, ResetPasswordPayload } from "../interface";
import api from "../api";
import {
  bootstrap,
  logout,
  login,
  registration,
  forgotPassword,
  resetPassword,
  getUser,
  checkUserPaymentMethod,
  getCheckoutUrl,
  goToBillingPortal,
} from "./actions";

function* loginUser(token: string) {
  yield tokenHandler.set(token);
  yield put(navigate("/"));
  yield put(login.success());
}

function* clearLocalStorage() {
  yield localStorage.clear();
}

/**
 * Restores the session on a cold start. The access token is kept in memory only, so it is gone
 * after a reload and has to be re-issued from the http-only refresh token cookie. The readable
 * CSRF cookie is set alongside it, so its absence means there is no session to restore and the
 * visitor is spared a refresh call that can only fail.
 */
function* bootstrapSaga() {
  if (tokenHandler.get()) {
    yield put(bootstrap.success({ authentificated: true }));
    return;
  }

  if (!hasCookie(CSRF_COOKIE_NAME)) {
    yield put(bootstrap.success({ authentificated: false }));
    return;
  }

  try {
    yield call(refreshAccessToken);
    yield put(bootstrap.success({ authentificated: true }));
  } catch (error) {
    console.warn("Token refresh failed during bootstrap", error);

    yield put(bootstrap.success({ authentificated: false }));
  }
}

function* logoutSaga() {
  try {
    // Revokes every refresh token of the user and clears the auth cookies. Without the CSRF
    // cookie there is no session to end and the call could only be rejected.
    if (hasCookie(CSRF_COOKIE_NAME)) {
      yield call(api.logout);
    }
  } catch {
    // The session is dropped on the client either way.
  } finally {
    yield tokenHandler.clear();
    yield clearLocalStorage();
    yield put(logout.success());
    yield put(navigate(NameOfRoutes.AUTH_LOGIN));
  }
}

function* loginSaga({ payload }: ActionWithPayload<LoginShape>) {
  try {
    yield put(startLoading());
    yield clearLocalStorage();
    const { token }: { token: string } = yield call(api.login, payload);
    yield put(stopLoading());
    yield loginUser(token);
  } catch (error) {
    yield put(login.failure(error as Error));
    yield put(stopLoading());
  }
}

function* registrationSaga({ payload }: ActionWithPayload<RegistrationShape>) {
  try {
    yield put(startLoading());
    const { token }: { token: string } = yield call(api.registration, payload);
    yield loginUser(token);
  } catch (error) {
    yield put(registration.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* forgotPasswordSaga({ payload }: ActionWithPayload<RestoreShape>) {
  try {
    yield put(startLoading());
    const response: { email: string } = yield call(api.forgotPassword, payload);
    yield put(forgotPassword.success(response));
    yield put(
      showNotification({
        message: "Email sent successfully",
        appearance: "success",
      }),
    );
  } catch (error) {
    const err = error as ResponseError;
    yield put(forgotPassword.failure(error as Error));
    if (err.code === 404) {
      yield put(
        showNotification({
          message: "Email sent successfully",
          appearance: "success",
        }),
      );
      return;
    }
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* resetPasswordSaga({ payload }: ActionWithPayload<ResetPasswordPayload>) {
  try {
    yield put(startLoading());
    const { token }: { token: string } = yield call(api.resetPassword, payload);
    yield loginUser(token);
  } catch (error) {
    yield put(resetPassword.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getUserSaga() {
  try {
    yield put(startLoading());
    const { user }: { user: UserModel } = yield call(api.getUser);
    yield put(getUser.success({ user }));
  } catch (error) {
    yield put(getUser.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* checkUserPaymentMethodSaga({ payload }: ActionWithPayload<WithCallback<{ id: number }, boolean>>) {
  try {
    yield put(startLoading());
    const response: { exists: boolean } = yield call(api.checkUserPaymentMethod, payload.id);
    yield put(checkUserPaymentMethod.success(response));
    payload.cb?.(response.exists);
  } catch (error) {
    yield put(checkUserPaymentMethod.failure(error as Error));
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* getCheckoutUrlSaga({
  payload,
}: ActionWithPayload<{ id: number; successUrl: string; cancelUrl: string; caseId: number }>) {
  try {
    yield put(startLoading());
    const { url }: { url: string } = yield call(api.getCheckoutUrl, {
      id: payload.id,
      successUrl: payload.successUrl,
      cancelUrl: payload.cancelUrl,
      caseId: payload.caseId,
    });
    window.open(url, "_self")?.focus();
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError));
  } finally {
    yield put(stopLoading());
  }
}

function* goToBillingPortalSaga({ payload }: ReturnType<typeof goToBillingPortal>) {
  try {
    yield put(startLoading());
    const { url }: { url: string } = yield call(api.getBillingPortalUrl, payload);
    location.replace(url);
  } catch (error) {
    yield put(showHttpErrorNotification(error as ResponseError, "Can't open billing portal. Please try again later."));
  } finally {
    yield put(stopLoading());
  }
}

function* authSagas() {
  yield takeLatest(bootstrap.request, bootstrapSaga);
  yield takeLatest(logout.request, logoutSaga);
  yield takeLatest(login.request, loginSaga);
  yield takeLatest(registration.request, registrationSaga);
  yield takeLatest(forgotPassword.request, forgotPasswordSaga);
  yield takeLatest(resetPassword.request, resetPasswordSaga);
  yield takeLatest(getUser.request, getUserSaga);
  yield takeLatest(goToBillingPortal, goToBillingPortalSaga);
  yield takeLatest(checkUserPaymentMethod.request, checkUserPaymentMethodSaga);
  yield takeLatest(getCheckoutUrl.request, getCheckoutUrlSaga);
}

export default authSagas;
